import { DataKiParams } from '../dataKi';
import { DkSisSearchParams } from '../dkSis';
import { MAIN_FIELD_TYPE, MainFieldType } from '../schemas';

/** 適正入賞Sチェックのマーキング code（BE の properNyushoSCheck） */
export const PROPER_NYUSHO_S_MARKING = 'properNyushoSCheck' as const;

/**
 * 入賞Sの3項目と上限・下限入賞Sの条件を足す既存PPMマーキング。
 * code は domain/marking.tsx の PPM_* と揃える。
 */
export const PPM_MARKINGS_FOR_NYUSHO_S = [
  'cashCow',
  'starCheck',
  'dog',
  'questionMarkCheck',
] as const;

/** 入賞Sの右に出す SIS 上下限の3項目 */
export const PROPER_NYUSHO_S_FIELD_CODES = [
  'sisNyushoSLowerLimit',
  'sisNyushoSUpperLimit',
  'sisNyushoSLimitDiff',
] as const;

export type ProperNyushoSFieldCode =
  (typeof PROPER_NYUSHO_S_FIELD_CODES)[number];

const NYUSHO_S_FIELD_CODE = 'nyushoS';
const SIS_NYUSHO_S_FIELD_CODE = 'sisNyushoS';

/** SIS 入賞S別データが非対応の出力 */
const SIS_UNSUPPORTED_MAIN_FIELDS: readonly MainFieldType[] = [
  MAIN_FIELD_TYPE.TYPE,
  MAIN_FIELD_TYPE.MAKER,
  MAIN_FIELD_TYPE.MAKER_GROUP,
  MAIN_FIELD_TYPE.KITAG,
];

const PROPER_NYUSHO_S_FIELD_CODE_SET = new Set<string>(
  PROPER_NYUSHO_S_FIELD_CODES
);

export const isProperNyushoSFieldCode = (
  code: string
): code is ProperNyushoSFieldCode => PROPER_NYUSHO_S_FIELD_CODE_SET.has(code);

/** 既存の条件に上限・下限入賞S（平日/土日祝・損益分岐・ランク帯）を追加するマーキング */
export const showsNyushoSLimitConditions = (marking?: string): boolean =>
  marking === PROPER_NYUSHO_S_MARKING ||
  (PPM_MARKINGS_FOR_NYUSHO_S as readonly string[]).includes(marking ?? '');

/**
 * マーキング適用時、DK-SIS が未指定なら ON にする対象。
 * sis*、適正入賞Sチェック、PPM。
 */
export const enablesDkSisOnMarkingApply = (marking?: string): boolean =>
  (marking?.startsWith('sis') ?? false) || showsNyushoSLimitConditions(marking);

/** 3項目の自動追加をBEに任せるマーキング（適正入賞SチェックとPPM） */
const requestsProperNyushoSColumns = showsNyushoSLimitConditions;

type NyushoSLimitConditions = Pick<
  DkSisSearchParams,
  'heiKyuKbn' | 'sisNyushoSBreakEvenForPachinko' | 'sisNyushoSRank'
>;

/**
 * 上限・下限入賞Sの初回リクエスト。
 * 平日、パチンコ11割（sisBreakEvenForPachinko の 06）、高ランク。
 */
const DEFAULT_NYUSHO_S_LIMIT_CONDITIONS: NyushoSLimitConditions = {
  heiKyuKbn: '1',
  sisNyushoSBreakEvenForPachinko: '06',
  sisNyushoSRank: 3,
};

/**
 * 適正入賞SチェックとPPMでは従来条件と入賞用条件を併用する。
 * それ以外では入賞用の3条件を外し、条件欄と送るパラメータを一致させる。
 */
export const dkSisConditionOverrideForMarking = (
  marking?: string
): NyushoSLimitConditions => {
  if (showsNyushoSLimitConditions(marking)) {
    return {};
  }
  return {
    heiKyuKbn: undefined,
    sisNyushoSBreakEvenForPachinko: undefined,
    sisNyushoSRank: undefined,
  };
};

/**
 * マーキング適用時の上限・下限入賞S条件。
 * 適正入賞SチェックとPPMへ初めて入るときは推奨の3条件を送る。
 * すでにそのマーキング中なら、パネルで指定した値を維持する。
 */
export const resolveNyushoSLimitConditionsOnMarkingApply = (input: {
  marking?: string;
  previousMarking?: string;
}): NyushoSLimitConditions => {
  if (!showsNyushoSLimitConditions(input.marking)) {
    return dkSisConditionOverrideForMarking(input.marking);
  }
  if (showsNyushoSLimitConditions(input.previousMarking)) {
    return {};
  }
  return { ...DEFAULT_NYUSHO_S_LIMIT_CONDITIONS };
};

export const isSisResultOn = (sisFieldTypes?: readonly string[]): boolean =>
  (sisFieldTypes ?? []).includes('result');

export const isSupportedProperNyushoSMainField = (
  mainField?: string
): boolean =>
  mainField === undefined ||
  !(SIS_UNSUPPORTED_MAIN_FIELDS as readonly string[]).includes(mainField);

const sameIdList = (
  left: readonly string[] | undefined,
  right: readonly string[] | undefined
): boolean => {
  const leftIds = [...(left ?? [])].sort();
  const rightIds = [...(right ?? [])].sort();
  return (
    leftIds.length === rightIds.length &&
    leftIds.every((id, index) => id === rightIds.at(index))
  );
};

/** 直前レスポンスの表示項目を土台にしてよいか（出力・種別が同じときだけ） */
export const isSameKiFieldContext = (
  request: Pick<DataKiParams, 'mainField' | 'shuList' | 'shuGroupIds'>,
  previous: Pick<DataKiParams, 'mainField' | 'shuList' | 'shuGroupIds'>
): boolean =>
  (request.mainField ?? MAIN_FIELD_TYPE.KI_TUSHO_MEI) ===
    (previous.mainField ?? MAIN_FIELD_TYPE.KI_TUSHO_MEI) &&
  sameIdList(request.shuList, previous.shuList) &&
  sameIdList(request.shuGroupIds, previous.shuGroupIds);

export const stripProperNyushoSFields = (
  fields: string[] | undefined
): string[] | undefined => {
  if (fields === undefined) {
    return undefined;
  }
  if (!fields.some(isProperNyushoSFieldCode)) {
    return fields;
  }
  return fields.filter((code) => !isProperNyushoSFieldCode(code));
};

/**
 * 列順に無い上限・下限入賞Sの3列を、SIS入賞Sの直後へ足す。
 * SIS入賞Sが無いときは入賞Sの直後へ足す。
 * どちらも列順に無いときは、未配置の入賞S・SIS入賞S・3列の順で先頭列の直後へ置く。
 * すでに列順にある列は動かさない。
 */
export const insertMissingProperNyushoSColumnsOrder = (
  columnsOrder: readonly string[],
  fieldCodes: readonly string[]
): string[] => {
  const fieldCodeSet = new Set(fieldCodes);
  const orderSet = new Set(columnsOrder);
  const missing = PROPER_NYUSHO_S_FIELD_CODES.filter(
    (code) => fieldCodeSet.has(code) && !orderSet.has(code)
  );
  if (missing.length === 0) {
    return [...columnsOrder];
  }

  const next = [...columnsOrder];
  const nyushoIndex = next.indexOf(NYUSHO_S_FIELD_CODE);
  const sisNyushoIndex = next.indexOf(SIS_NYUSHO_S_FIELD_CODE);
  if (nyushoIndex !== -1) {
    const insertAt =
      sisNyushoIndex !== -1 ? sisNyushoIndex + 1 : nyushoIndex + 1;
    next.splice(insertAt, 0, ...missing);
    return next;
  }

  const nyushoMissing =
    fieldCodeSet.has(NYUSHO_S_FIELD_CODE) && !orderSet.has(NYUSHO_S_FIELD_CODE);
  if (sisNyushoIndex !== -1 && nyushoMissing) {
    next.splice(sisNyushoIndex, 0, NYUSHO_S_FIELD_CODE);
    next.splice(sisNyushoIndex + 2, 0, ...missing);
    return next;
  }
  if (sisNyushoIndex !== -1) {
    next.splice(sisNyushoIndex + 1, 0, ...missing);
    return next;
  }

  const sisMissing =
    fieldCodeSet.has(SIS_NYUSHO_S_FIELD_CODE) &&
    !orderSet.has(SIS_NYUSHO_S_FIELD_CODE);
  const block = [
    ...(nyushoMissing ? [NYUSHO_S_FIELD_CODE] : []),
    ...(sisMissing ? [SIS_NYUSHO_S_FIELD_CODE] : []),
    ...missing,
  ];
  const nameIndex = next.indexOf(fieldCodes.at(0) ?? '');
  const insertIndex = nameIndex === -1 ? 0 : nameIndex + 1;
  next.splice(insertIndex, 0, ...block);
  return next;
};

/**
 * 入賞Sの直後（無ければ SIS入賞S の直後、それも無ければ末尾）へ3項目を置く
 */
export const insertProperNyushoSFields = (
  fields: readonly string[]
): string[] => {
  const base = fields.filter((code) => !isProperNyushoSFieldCode(code));
  const anchorCode = base.includes(NYUSHO_S_FIELD_CODE)
    ? NYUSHO_S_FIELD_CODE
    : base.includes(SIS_NYUSHO_S_FIELD_CODE)
    ? SIS_NYUSHO_S_FIELD_CODE
    : undefined;
  const anchorIndex =
    anchorCode === undefined ? undefined : base.indexOf(anchorCode);
  const insertIndex = anchorIndex === undefined ? base.length : anchorIndex + 1;
  return [
    ...base.slice(0, insertIndex),
    ...PROPER_NYUSHO_S_FIELD_CODES,
    ...base.slice(insertIndex),
  ];
};

const needsProperNyushoSFields = (
  fields: readonly string[],
  marking?: string
): boolean =>
  fields.includes(NYUSHO_S_FIELD_CODE) || requestsProperNyushoSColumns(marking);

/**
 * /data/ki に渡す fields。
 * DK-SIS実績がONで入賞Sを出しているとき、または適正入賞S / PPMマーキングのときに3項目を足す。
 * fields 未指定かつマーキング適用時は undefined のままにし、列追加は BE に任せる。
 */
export const resolveProperNyushoSRequestFields = (input: {
  fields: string[] | undefined;
  sisFieldTypes?: string[];
  marking?: string;
  mainField?: string;
  previousFields?: string[];
  sameFieldContext: boolean;
}): string[] | undefined => {
  const stripped = stripProperNyushoSFields(input.fields);
  const sisOn = isSisResultOn(input.sisFieldTypes);
  const supported = isSupportedProperNyushoSMainField(input.mainField);

  if (!sisOn || !supported) {
    return stripped;
  }

  if (stripped !== undefined) {
    return needsProperNyushoSFields(stripped, input.marking)
      ? insertProperNyushoSFields(stripped)
      : stripped;
  }

  if (requestsProperNyushoSColumns(input.marking)) {
    return undefined;
  }

  const previous = stripProperNyushoSFields(input.previousFields);
  if (
    input.sameFieldContext &&
    previous !== undefined &&
    previous.includes(NYUSHO_S_FIELD_CODE)
  ) {
    return insertProperNyushoSFields(previous);
  }

  return undefined;
};

/**
 * 表示項目未指定の初回応答で入賞Sが含まれていたとき、3項目を足して取り直す
 */
export const shouldRetryProperNyushoSFields = (input: {
  requestedFields: string[] | undefined;
  responseFields: string[] | undefined;
  sisFieldTypes?: string[];
  marking?: string;
  mainField?: string;
}): boolean => {
  if (input.requestedFields !== undefined) {
    return false;
  }
  if (!isSisResultOn(input.sisFieldTypes)) {
    return false;
  }
  if (!isSupportedProperNyushoSMainField(input.mainField)) {
    return false;
  }
  if (requestsProperNyushoSColumns(input.marking)) {
    return false;
  }
  if (input.responseFields === undefined) {
    return false;
  }
  if (!input.responseFields.includes(NYUSHO_S_FIELD_CODE)) {
    return false;
  }
  return !input.responseFields.some(isProperNyushoSFieldCode);
};

/**
 * 初回 /data/ki の検索条件。fields が変わらないときは同じ参照を返す。
 */
export const applyProperNyushoSRequestFields = (
  params: DataKiParams,
  previousSetting:
    | Pick<DataKiParams, 'fields' | 'mainField' | 'shuList' | 'shuGroupIds'>
    | undefined
): DataKiParams => {
  const resolvedFields = resolveProperNyushoSRequestFields({
    fields: params.fields,
    sisFieldTypes: params.sisFieldTypes,
    marking: params.marking,
    mainField: params.mainField,
    previousFields: previousSetting?.fields,
    sameFieldContext:
      previousSetting !== undefined &&
      isSameKiFieldContext(params, previousSetting),
  });
  if (resolvedFields === params.fields) {
    return params;
  }
  return { ...params, fields: resolvedFields };
};

/**
 * 初回応答を見て取り直すときの検索条件。取り直さないときは undefined。
 */
export const retryProperNyushoSRequest = (
  requestParams: DataKiParams,
  responseFields: string[] | undefined
): DataKiParams | undefined => {
  if (
    !shouldRetryProperNyushoSFields({
      requestedFields: requestParams.fields,
      responseFields,
      sisFieldTypes: requestParams.sisFieldTypes,
      marking: requestParams.marking,
      mainField: requestParams.mainField,
    })
  ) {
    return undefined;
  }
  return {
    ...requestParams,
    fields: insertProperNyushoSFields(
      stripProperNyushoSFields(responseFields) ?? []
    ),
  };
};
